package org.multiplatform.kotfolio.pages.kmm

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

@Page("/kmm/basic_component")
@Composable
fun KMPBasicComponentPage() {
    BlogLayout(
        PageProperties(
            "Basic",
            ""
        ),
        BlogProperties("KMP", "Basic")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Basic syntax",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Basic
                    var goToSetting: () -> Void = {}
                    var foundItem: String? = nil
                    private var currentPage = 1
                    @Published var newItemName: String = ""
                    @Published var quantity: Int = 1
                        
                    //default value
                    let actualFoundItem = foundItem ?? "empty"
                        
                    //control flow
                    let description = if delta <= 10 {
                      "low"
                    } else if delta >= 50 {
                      "high"
                    } else {
                      "medium"
                    }
                        
                    //function
                    func addTwoNumbers(a: Int, b: Int) -> Int {
                        return a + b
                    }
                    
                    //If let
                    let fetchedUserId: String? = "Optional id of the fetched user"
                    if let userId = fetchedUserId {
                        // userId can be used as non-optional constant
                        print(userId)
                    } else {
                        // fetchedUserId is nil/null
                        throw Error("Missing user id")
                    }
                    
                    let fetchedUserId: String? = "Optional id of the fetched user"
                    if let fetchedUserId {
                        // fetchedUserId can be used as non-optional constant
                        print(fetchedUserId)
                    }
                    
                    //Guard 
                    func checkUsernameValid(username: String?): Bool {
                        guard let username else {
                            // username is nil, can't evaluate
                            return false
                        }
                        // username can be used non-optional
                        return username.count > 3
                    }
                    
                    
                    //ViewModel Access
                    import Foundation
                    import shared

                    extension HomeViewModel {
                        @MainActor class HomeViewModel: ObservableObject {
        
                        private let getMoviesUseCase = GetMoviesUseCase.init()
        
                        @Published private(set) var movies:[Movie] = []
                        @Published private(set) var isLoading: Bool = false
        
                        private var currentPage = 1
                        var goToSetting: () -> Void = {}
                        
                        @Published var newItemName: String = ""
                        @Published var quantity: Int = 1
                        
                        @Published var tabIndex: FamilyListPageTabEnum = .pending {
                            didSet {
                                Task { await loadData(fromNetwork: false, showLoading: false) }
                            }
                        }
                    }
                    
                    //Swift UI
                    struct HomeScreen: View {
                        @StateObject var viewModel = HomeViewModel()
                    }
                    
                    //Class Access
                    class CountModel(var count : Int=0) {
                        fun increment(){
                            count++
                        }
                        fun decrement(){
                            if (count>0){
                                count--
                            }
                        }
                    }
                    
                    //Swift UI
                    @State var counter = CountModel(count:0)
                    @State private var count: Int32 = 0
                    
                    //Jetpack Compose
                    val count = remember { CountModel() }
                    
                    var counting by remember { mutableIntStateOf(count.count) }
                """.trimIndent()
            )

            SpanText(
                text = "Counter App",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //commonMain
                    class CountModel(var count : Int=0) {
                        fun increment(){
                            count++
                        }
                        fun decrement(){
                            if (count>0){
                                count--
                            }
                        }
                    }
                    
                    //iosApp
                    import SwiftUI
                    import shared

                    struct ContentView: View {
                        @State var counter = CountModel(count:0)
                        @State private var count: Int32 = 0
                    	var body: some View {
                            VStack(content: {
                                Text("Count: \(count)").padding(20)
                                Button("Increment") {
                                               counter.increment()
                                               count = counter.count
                                           }
                                           Button("Decrement") {
                                               counter.decrement()
                                               count = counter.count
                                           }.padding(10)
                            })
                            .frame(minWidth: 0, maxWidth: .infinity, minHeight: 0, maxHeight: .infinity).background(Color.white)
                    	}
                    }

                    struct ContentView_Previews: PreviewProvider {
                    	static var previews: some View {
                    		ContentView()
                    	}
                    }
                    
                    //Jetpack Compose
                    @Composable
                    fun CountingScreen() {
                        CountingScreenUi()
                    }
                    @Composable
                    fun CountingScreenUi() {
                        val count = remember {
                            CountModel()
                        }

                        var counting by remember {
                            mutableIntStateOf(count.count)
                        }

                        Column(
                            modifier = Modifier.fillMaxSize().background(Color.Black),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.Center) {

                            Text(text = counting.toString(), color = Color.White, fontSize = 20.sp)

                            Button(onClick = {
                                    count.increment()
                                    counting = count.count},
                                modifier = Modifier
                                    .padding(20.dp)
                                    .fillMaxWidth()) {
                                Text(text = "Increment")
                            }

                            Button(onClick = {
                                    count.decrement()
                                    counting = count.count
                                }, modifier = Modifier
                                    .padding(start = 20.dp, end = 20.dp)
                                    .fillMaxWidth()) {
                                Text(text = "Decrement")
                            }
                        }
                    }

                    @Composable
                    @Preview(showSystemUi = true)
                    fun CountingScreenUiPreview() {
                        CountingScreenUi()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Collection Framework",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //map
                    func logEvent(name: String, params: [String : Any]) {
                        Analytics.logEvent(name, parameters: params)
                    }
                    
                    private var analytics = Firebase.analytics                       
                    fun logEvent(name: String, params: Map<String, Any>) { 
                        val bundle = params.toBundle()
                        analytics.logEvent(name, bundle)
                    }
                    private fun Map<String, Any?>.toBundle(): Bundle = bundleOf(*this.toList().toTypedArray())    
                        
                    """.trimIndent()
            )

            SpanText(
                text = "Argument function UI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    //Swift UI
                    import Foundation
                    import SwiftUI

                    struct QuantitySelectionView: View {
                        @State private var quantity: Int
                        private let minValue: Int
                        private let maxValue: Int
                        private let incValue: Int
                        private let onValueChanged: (Int) -> Void

                        init(
                            value: Int,
                            minValue: Int = 1, maxValue: Int = 50, incValue: Int = 1, onValueChanged: @escaping (Int) -> Void) {
                            self._quantity = State(initialValue: value)
                            self.minValue = minValue
                            self.maxValue = maxValue
                            self.incValue = incValue
                            self.onValueChanged = onValueChanged
                        }

                        var body: some View {

                        }
                    }
                    
                    //Jetpack Compose
                    @Composable
                    fun QuantitySelectionView(
                        value: Int,
                        incValue: Int = 1,
                        minValue: Int = Int.MIN_VALUE,
                        maxValue: Int = Int.MAX_VALUE,
                        coroutineScope: CoroutineScope,
                        modifier: Modifier = Modifier,
                        onValueChanged: (newValue: Int) -> Unit
                    ) { }
                    """.trimIndent()
            )

            SpanText(
                text = "Loading state Swift UI",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = $$"""
                    @Published var viewState: FamilyListViewState = .initial
                    
                    enum FamilyListViewState: Equatable {
                        case initial
                        case loading
                        case success
                        case error(
                            message: String,
                            retryAction: (() -> Void)?
                        )

                        static func == (lhs: FamilyListViewState, rhs: FamilyListViewState) -> Bool {
                            switch (lhs, rhs) {
                            case (.initial, .initial):
                                return true
                            case (.loading, .loading):
                                return true
                            case (.success, .success):
                                return true
                            case (.error, .error):
                                return true
                            default:
                                return false
                            }
                        }
                    }
                    
                    //model
                    import shared

                    extension FamilyListModel: Identifiable {
                        
                        public var id: String {
                            self.uuid
                        }
                        
                        convenience init(
                            name: String,
                            isCompleted: Bool = false,
                            isCompletedDate: Kotlinx_datetimeLocalDateTime? = nil,
                            isPrioritized: Bool = false,
                            quantity: Int32 = 1,
                            price: Double = 0,
                            product: ProductModel? = nil
                        ) {
                            self.init(
                                uuid: "",
                                name: name,
                                isCompleted: isCompleted, 
                                isCompletedDate: isCompletedDate,
                                isPrioritized: isPrioritized,
                                quantity: quantity,
                                price: price,
                                product: product
                            )
                        }
                    }
                    
                    //observable 
                    import Foundation
                    import shared

                    protocol FamilyListViewModelProtocol: ObservableObject {
                        var viewState: FamilyListViewState { get set }
                        var familyListModels: [FamilyListModel] { get set }
                        var goToSetting: () -> Void { get set }
                        func loadData(fromNetwork: Bool, showLoading: Bool) async
                        
                        //var isLoading: Bool { get }
                        //isLoading = true
                        //DispatchQueue.main.async { self.isLoading = false }
                        //DispatchQueue.main.async { [self] in isLoading = true  }
                    }
                    
                    //mocked
                    import Foundation
                    import shared

                    class FamilyListViewModelMocked: FamilyListViewModelProtocol {
                        var viewState: FamilyListViewState = .success

                        var familyListModels: [FamilyListModel] = [
                            FamilyListModel(uuid: "", name: "Item 01", isCompleted: false, isCompletedDate: nil, isPrioritized: false, quantity: 1, price: 0, product: nil),
                        ]
                    }
                        
                    //viewmodel
                    import Foundation
                    import shared
                    import KMPNativeCoroutinesAsync

                    class FamilyListViewModel: FamilyListViewModelProtocol {
                        @Published
                        var viewState: FamilyListViewState = .initial
                        @Published
                        var familyListModels: [FamilyListModel] = []
                        @Published
                        var isShowingBarcodeBottomSheet: Bool = false
                        @Published
                        var newItemName: String = ""
                        @Published
                        var quantity: Int = 1
                        @Published
                        var tabIndex: FamilyListPageTabEnum = .pending
                        {
                            didSet {
                                Task { await loadData (fromNetwork: false, showLoading: false) }
                            }
                        }

                        var goToSetting: () -> Void = {}
                        var goToQuickInsert: () -> Void = {}
                        var goToBarcodeScanner: () -> Void = {}

                        private var accountModel: AccountModel?

                        private let getAllFamilyListUseCase: GetAllFamilyListUseCase
                        init(
                              getAllFamilyListUseCase: GetAllFamilyListUseCase
                        ) {
                              self.getAllFamilyListUseCase = getAllFamilyListUseCase
                        }
                        
                        @MainActor
                        func loadData(fromNetwork: Bool, showLoading: Bool) async {
                            if (showLoading) {
                                viewState = .loading
                            }

                            do {
                                accountModel = try await asyncFunction(for: getOrCreateAccountFromLocalUuidUseCase.execute())
                            } catch {
                                showError(e: error)
                                return
                            }
                            
                            if fromNetwork {
                                do {
                                    let result = try await asyncFunction(for: getAllFamilyListUseCase.execute())
                                    familyListModels = result.sorted { $0.name.lowercased() < $1.name.lowercased() }
                                } catch {
                                    showError(e: error)
                                    return
                                }
                            }

                            if (showLoading) {
                                viewState = .success
                            }
                        }
                        
                        func showError(e: Error) {
                            crashlytics.record(error: e)
                            viewState = .error(message: e.localizedDescription, retryAction: {
                                Task {
                                    await self.loadData(fromNetwork: true, showLoading: true)
                                }
                            })
                        }
                        
                        func remove(uuid: String) async {
                            if let index = familyListModels.firstIndex(where: { $0.uuid == uuid }) {
                                viewState = .loading
                                do {
                                    _ = try await asyncFunction(for: deleteFamilyListUseCase.execute(uuid: uuid))
                                    familyListModels.remove(at: index)
                                    await loadData(fromNetwork: false, showLoading: false)
                                } catch {
                                    showError(e: error)
                                    return
                                }
                                viewState = .success
                            }
                        }
                        
                        @MainActor
                        func updateIsCompleted(uuid: String, isCompleted: Bool) async {
                            if let index = familyListModels.firstIndex(where: { $0.uuid == uuid }) {
                                familyListModels[index].isCompleted = isCompleted
                                if isCompleted {
                                    familyListModels[index].isPrioritized = false
                                }
                                await update(item: familyListModels[index])
                                await loadData(fromNetwork: false, showLoading: true)
                            }
                        }
                        
                        @MainActor
                        private func update(item: FamilyListModel) async {
                            do {
                                _ = try await asyncFunction(for: updateFamilyListUseCase.execute(item: item))
                            } catch {
                                showError(e: error)
                                return
                            }
                        }
                    }
                    
                    //view
                    import SwiftUI
                    import shared
                    import Foundation

                    struct FamilyListPage<ViewModel>: View where ViewModel: FamilyListViewModelProtocol {

                        @StateObject var viewModel: ViewModel

                        var body: some View {
                            ZStack {
                                switch viewModel . viewState {
                                    case let . error (message, retryAction):
                                    ErrorPage(message: message, retryAction: retryAction)
                                    default:
                                    FamilyListSuccessView()
                                }

                                if viewModel.viewState == . loading {
                                    ProgressView()
                                }
                            }
                        }

                        private func FamilyListSuccessView() -> some View {

                        }
                    }
                        
                    //ListView    
                    import SwiftUI
                    import shared

                    struct FamilyListView<ViewModel>: View where ViewModel: FamilyListViewModelProtocol {
                        @StateObject var viewModel: ViewModel
                        let items: [FamilyListModel]
                        let refreshData: (_ showLoading: Bool) -> Void
                        var body: some View {
                            Group {
                                if items.isEmpty {
                                    FamilyListEmptyState(action: {
                                        refreshData(true)
                                    })
                                } else {
                                    List(items) { item in
                                            FamilyListRowItemView(
                                                viewModel: viewModel,
                                                item: item
                                            )
                                                .background(Color.white)
                                                .cornerRadius(10)
                                                .shadow(radius: 1)
                                        .swipeActions(edge: .trailing) {
                                            Button(role: .destructive) {
                                            Task { await viewModel.remove(uuid: item.uuid) }
                                        } label: {
                                            Label("Delete", systemImage: SystemName.trash.rawValue)

                                        }.background(.red)
                                        }
                                            .hideRowSeparator()
                                    }
                                        .refreshable {
                                            refreshData(false)
                                        }
                                        .backgroundClear()
                                }
                            }
                        }
                    }
                    
                    //router
                    private func FamilyListPage(router: ViewRouter) -> some View {
                        let viewModel: FamilyListViewModel = ResolverApp().resolve()
                        viewModel.goToSetting = { self.router = .settings }
                        viewModel.goToQuickInsert = { self.router = .quickInsert }
                        return Family_List.FamilyListPage(viewModel: viewModel)
                    }
                    
                    """.trimIndent()
            )

            SpanText(
                text = "ViewModel",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import Foundation
                    import shared

                    extension HomeScreen {
                        @MainActor class HomeViewModel: ObservableObject {
                            
                            private let getMoviesUseCase = GetMoviesUseCase.init()
                            
                            @Published private(set) var movies:[Movie] = []
                            @Published private(set) var isLoading: Bool = false
                            
                            private var currentPage = 1
                            private(set) var loadFinished: Bool = false
                            
                            func loadMovies() async {
                                if isLoading {
                                    return
                                }
                                
                                do {
                                    
                                    let movies = try await getMoviesUseCase.invoke(page: Int32(currentPage))
                                    isLoading = false
                                    loadFinished = movies.isEmpty
                                    
                                    currentPage += 1
                                    
                                    self.movies = self.movies + movies
                                    
                                } catch {
                                    isLoading = false
                                    loadFinished = true
                                }
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    import Foundation
                    import shared

                    extension NewsScreen{
                        @MainActor class NewsViewModel: ObservableObject {
                            @Published var isLoading = true
                            @Published var newsList: [NewsModel]
                            @Published var errorMessage: String? = nil

                            init() {
                                newsList = [NewsModel]()
                            }

                            func getNews(category: String){
                                NewsHelper().getNews(category: category) { newsList, err in
                                    DispatchQueue.main.async {
                                        if let newsList = newsList {
                                            self.newsList = newsList
                                            self.isLoading = false
                                            print(newsList)
                                        }
                                        if let err = err {
                                            self.errorMessage = err.localizedDescription
                                            self.isLoading = false
                                            print(err.localizedDescription)
                                        }
                                    }
                                }

                            }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "GridView",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    import SwiftUI
                    import shared

                    struct HomeScreen: View {
                        @StateObject var viewModel = HomeViewModel()
                        
                        let gridColumns: [GridItem] = Array(repeating: GridItem(.flexible(), spacing: 16), count: 2)
                        
                        var body: some View {
                            NavigationStack{
                                ScrollView{
                                    LazyVGrid(columns: gridColumns, spacing: 16){
                                        
                                        ForEach(viewModel.movies, id: \.id){ movie in

                                        }
                                    }
                                }
                                .navigationTitle("Movies")
                            }
                            .task {
                                await viewModel.loadMovies()
                            }
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Pagination
                    
                    struct PokemonListItem: View {
                        let pokemon: ListPokemonItem
                        private let imageSize: CGFloat = 64

                        var body: some View {
                            HStack {
                                PokeballPlaceholderImage(url: pokemon.iconURL)
                                    .frame(width: imageSize, height: imageSize)

                                Text(pokemon.name)
                                    .font(.caption)
                            }
                            .cardViewModifier()
                        }
                    }

                    #Preview {
                        PokemonListItem(pokemon: .companion.list.first!)
                    }
                    
                    import shared
                    import SwiftUI

                    struct PokemonListView: View {
                        let pokemon: [ListPokemonItem]
                        let isLoadingMore: Bool
                        let loadMore: () -> Void
                        var body: some View {
                            ScrollView {
                                LazyVGrid(columns: [GridItem(.flexible()), GridItem(.flexible())], spacing: 16) { // Can be done with Array repeating too
                                    ForEach(pokemon, id: \.name) { item in
                                        NavigationLink(destination: {
                                            DetailScreen(viewModel: .init(url: item.passThroughURL))
                                        }, label: {
                                            PokemonListItem(pokemon: item)
                                                .tint(.black)
                                        })
                                        .task {
                                            if item.name == pokemon.last?.name {
                                                loadMore()
                                            }
                                        }
                                    }
                                }
                                ProgressView("Loading more...")
                            }
                        }
                    }

                    #Preview {
                        PokemonListView(pokemon: ListPokemonItem.companion.list, isLoadingMore: true) {
                            print("Load more")
                        }
                        .previewLayout(.sizeThatFits)
                    }
                    
                    import shared
                    import SwiftUI

                    struct HomeScreen: View {
                        @StateObject private var viewModel = HomeScreenViewModel()
                        var body: some View {
                            NavigationStack {
                                switch viewModel.loadingState {
                                case .initialLoading:
                                    LoadingScreen()
                                case let .content(pokemon, isLoadingMore):
                                    PokemonListView(pokemon: pokemon, isLoadingMore:isLoadingMore) {
                                        Task {
                                            await viewModel.loadMorePokemon()
                                        }
                                    }
                                case let .error(msg):
                                    ErrorScreen(errorMsg: msg) {
                                        Task {
                                            viewModel.loadInitialPokemon
                                        }
                                    }
                                }
                            }
                            .task {
                                await viewModel.loadInitialPokemon()
                            }
                        }
                    }

                    #Preview {
                        HomeScreen()
                    }
                    
                    import Foundation
                    import shared
                    import SwiftUI

                    // MARK: - HomeScreenViewModel
                    final class HomeScreenViewModel: ObservableObject {
                        private let fetcher: PokemonFetcher = .init()
                        private var pokemonList: [ListPokemonItem] = []

                        @Published private(set) var loadingState: LoadingState = .initialLoading

                        @MainActor
                        func loadInitialPokemon() async {
                            do {
                                let fetchedPokemonList = try await fetcher.fetchPokemonList()
                                pokemonList.append(contentsOf: fetchedPokemonList)
                                loadingState = .content(pokemon: pokemonList, isLoadingMore: false)
                            } catch {
                                loadingState = .error(msg: error.localizedDescription)
                            }
                        }

                        @MainActor
                        func loadMorePokemon() async {
                            switch loadingState {
                            case let .content(pokemon, isLoadingMore):
                                if !isLoadingMore {
                                    loadingState = .content(pokemon: pokemon, isLoadingMore: true)
                                    await loadInitialPokemon()
                                }
                            default:
                                return
                            }
                        }
                    }

                    // MARK: HomeScreenViewModel.LoadingState
                    extension HomeScreenViewModel {
                        enum LoadingState {
                            case initialLoading
                            case content(pokemon: [ListPokemonItem], isLoadingMore: Bool = false)
                            case error(msg: String)
                        }
                    }
                    
                    import SwiftUI
                    struct LoadingScreen: View {
                        var body: some View {
                            VStack {
                                Text("Loading")
                                    .font(.title)
                                    .fontWeight(.bold)
                                    .italic()
                                ProgressView()
                                    .progressViewStyle(.circular)
                            }
                        }
                    }

                    #Preview {
                        LoadingScreen()
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        let columns = [ GidItem(.adaptive(minimum: 150)) ]
                        
                        Section {
                            LazyVGrid(columns: columns, spacing: 20) {
                                ForEach(viewModel.pokemonList, id: \.pokeId) { pokemon in
                                    
                                }
                            }
                            .padding(.all, 20)
                        }  
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Optional Display 
                    
                    struct NewsItem: View {
                        var article: Article // The news article to display

                        var body: some View {
                            VStack(alignment: .leading, spacing: 8) {
                                // Optionally display the author if available
                                if let author = article.author {
                                    Text("by \(author)")
                                        .font(.subheadline)
                                        .foregroundColor(.secondary)
                                }
                                
                                // Optionally display the article image if available
                                if let urlToImage = article.urlToImage, let imageUrl = URL(string: urlToImage) {
                                    AsyncImage(url: imageUrl) { image in
                                        image.resizable()
                                            .aspectRatio(contentMode: .fill)
                                    } placeholder: {
                                        // Placeholder for loading image
                                        Color.gray
                                    }
                                    .frame(height: 180)
                                    .clipped()
                                }
                            }
                            .padding(.vertical, 8) // Add vertical padding to the NewsItem view
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @MainActor
                    class LoginViewModel: ObservableObject {
                        @Published var isLoading = false {
                            didSet {
                                if !isLoading {
                                    isLoginLoading = false
                                    isLoginWithFBLoading = false
                                    isRegisterLoading = false
                                }
                            }
                        }
                        
                        func loginUser() {
                            guard !isLoading else { return }
                            Task {
                                do {
                                    let responseModel = try await usecase.verify(with: LoginModel(email: email))
                                    self.responseModel = responseModel
                                    isLoading = false
                                    authenticationSuccessful()
                                } catch {
                                    isLoading = false
                                }
                            }
                        }   
                    }   
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Observable class UserViewModel {
                        var name: String = ""
                    }
                    
                    struct ContentView: View {
                        @StateObject var viewModel = UserViewModel()
                        var body: some View {
                            Text(viewModel.name)
                        }
                    }
                    """.trimIndent()
            )
        }
    }
}

